import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, CheckCircle, Loader2 } from "lucide-react";
import { sendLeadToCMS } from "@/lib/sendLeadToCMS";

export const QuoteRequestDialog = ({ open, onOpenChange, quoteData }) => {
  const [contactInfo, setContactInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactInfo.fullName || !contactInfo.email || !contactInfo.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSending(true);

    const fullQuote = {
      ...quoteData,
      ...contactInfo,
      submittedAt: new Date().toISOString(),
      id: Date.now()
    };

    const existing = JSON.parse(localStorage.getItem("tripsyfy_quotes") || "[]");
    existing.push(fullQuote);
    localStorage.setItem("tripsyfy_quotes", JSON.stringify(existing));

    // Send to CMS in background (fire-and-forget, never blocks user)
    const nameParts = (contactInfo.fullName || "").trim().split(/\s+/);
    const q = quoteData || {};
    sendLeadToCMS({
      first_name:       nameParts[0] || "",
      last_name:        nameParts.slice(1).join(" "),
      email:            contactInfo.email,
      phone:            contactInfo.phone,
      trip_type:        q.type || "contact",
      departure_city:   q.from || q.departure_city || "",
      destination_city: q.to || q.destination || q.region || "",
      departure_date:   q.departure || q.travelDate || q.departureDate || "",
      return_date:      q.returnDate || "",
      message:          contactInfo.message || "",
      num_adults:       parseInt(q.passengers || q.travelers || "1", 10) || 1,
      num_children:     0,
    });

    try {
      const res = await fetch("/api/quote-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fullQuote),
      });
      const data = await res.json();
      if (data.success) {
        console.log("[Quote] Email status:", data.emailStatus);
      }
    } catch (err) {
      console.warn("[Quote] API call failed, request still saved locally:", err.message);
    }

    setSending(false);
    setSubmitted(true);
    toast.success("Quote request submitted! We'll get back to you within 24 hours.");
    setTimeout(() => {
      setSubmitted(false);
      setContactInfo({ fullName: "", email: "", phone: "", message: "" });
      onOpenChange(false);
    }, 2000);
  };

  const getTypeLabel = () => {
    if (!quoteData) return "Quote";
    switch (quoteData.type) {
      case "flight": return "Flight Quote";
      case "tour": return "Tour Quote";
      case "cruise": return "Cruise Quote";
      default: return "Quote";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!submitted && !sending) onOpenChange(val); }}>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
            <h3 className="heading-sm text-center">Request Submitted!</h3>
            <p className="text-sm text-muted-foreground text-center">Our travel experts will contact you within 24 hours with a personalized quote. Check your email for a confirmation.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="heading-sm">{getTypeLabel()} Request</DialogTitle>
              <DialogDescription>Enter your contact details and we'll craft a personalized offer for you.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  name="name"
                  autoComplete="name"
                  placeholder="John Doe"
                  value={contactInfo.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  required
                  disabled={sending}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  spellCheck={false}
                  placeholder="john@example.com"
                  value={contactInfo.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  disabled={sending}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 (555) 123-4567"
                  value={contactInfo.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  required
                  disabled={sending}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Special Requests (Optional)</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Any specific preferences, dietary requirements, accessibility needs…"
                  value={contactInfo.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  rows={3}
                  disabled={sending}
                />
              </div>
              <Button type="submit" className="w-full active:scale-[0.97] transition-[background-color,transform] duration-150" size="lg" disabled={sending}>
                {sending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…</>
                ) : (
                  <><Send className="h-4 w-4" aria-hidden="true" /> Submit Quote Request</>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">We respect your privacy. No spam, ever.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
